import { reflect } from '@effector/reflect';
import { Menu, Avatar, Divider, Box, Button } from '@mantine/core';
import { Link } from 'atomic-router-react';
import { combine, StoreValue } from 'effector';
import { useUnit } from 'effector-react';
import { authModel, USER_ROLES } from 'shared/api';
import { $t, translated } from 'shared/lib';
import { accountRoute, userRoute } from 'shared/routing';
import { viewerModel } from '../../model';
import cls from './viewer-menu.module.scss';

export const ViewerMenuButton = reflect({
	view: Avatar<'button'>,
	bind: {
		component: 'button',
		'data-test-id': 'viewer-menu-button',
		children: viewerModel.$shortName,
		onClick: viewerModel.switcher.toggle,
	},
});

export const ViewerMenuAvatar = reflect({
	view: Avatar<'div'>,
	bind: {
		size: 'lg',
		className: cls.viewerMenuAvatar,
		children: viewerModel.$shortName,
	},
});

export const ViewerMenuName = reflect({
	view: Box<'p'>,
	bind: {
		component: 'p',
		className: cls.viewerMenuName,
		children: viewerModel.$name,
	},
});

export const ViewerMenuRole = reflect({
	view: Box<'span'>,
	bind: {
		component: 'span',
		className: cls.viewerMenuRole,
		children: combine([$t, authModel.$role], ([t, role]) => t(`user-roles.${role}`)),
	},
});

export const ViewerMenuAccountLink = reflect({
	view: Button<typeof Link>,
	bind: {
		component: Link,
		variant: 'ghost',
		'data-test-id': 'viewer-menu-account-link',
		to: accountRoute,
		query: viewerModel.$accountId.map((accountId) => ({ id: accountId ?? 0 })),
		className: cls.viewerMenuItem,
		children: translated('viewer-menu.account'),
		replace: combine(
			{
				id: viewerModel.$accountId,
				isOpened: accountRoute.$isOpened,
				idQuery: accountRoute.$query.map(({ id }) => id ?? null),
			},
			({ id, isOpened, idQuery }) => isOpened && id === idQuery,
		),
	},
});

export const ViewerMenuProfileLink = reflect({
	view: Button<typeof Link<StoreValue<typeof userRoute.$params>>>,
	bind: {
		component: Link,
		variant: 'ghost',
		'data-test-id': 'viewer-menu-profile-link',
		to: userRoute,
		params: viewerModel.$id.map((id) => ({ id: id ?? 0 })),
		className: cls.viewerMenuItem,
		children: translated('viewer-menu.profile'),
		replace: combine(
			{
				id: viewerModel.$id,
				isOpened: userRoute.$isOpened,
				idParam: userRoute.$params.map((params) => Number(params.id)),
			},
			({ id, isOpened, idParam }) => isOpened && id === idParam,
		),
	},
});

export const ViewerMenuLogoutButton = reflect({
	view: Button<'button'>,
	bind: {
		'data-test-id': 'viewer-menu-logout-button',
		variant: 'ghost',
		className: cls.viewerMenuItem,
		children: translated('viewer-menu.logout'),
		onClick: viewerModel.logout,
	},
});

export const ViewerMenuMenu = () => {
	const { active, disable, toggle } = useUnit(viewerModel.switcher);
	const role = useUnit(viewerModel.$role);

	return (
		<Menu
			opened={active}
			position='bottom-end'
			width={320}
			onClose={disable}
		>
			<Menu.Target>
				<ViewerMenuButton
					data-test-id='viewer-menu-button'
					onClick={toggle}
				/>
			</Menu.Target>
			<Menu.Dropdown className={cls.viewerMenuDropdown}>
				<header className={cls.viewerMenuHeader}>
					<ViewerMenuAvatar />
					<div className={cls.viewerMenuInfo}>
						<ViewerMenuName />
						<ViewerMenuRole />
					</div>
				</header>
				<div className={cls.viewerMenuBody}>
					{role !== USER_ROLES.cashier && (
						<>
							<div className={cls.viewerMenuGroup}>
								<ViewerMenuAccountLink />
								<ViewerMenuProfileLink />
							</div>
							<Divider />
						</>
					)}
					<div className={cls.viewerMenuGroup}>
						<ViewerMenuLogoutButton />
					</div>
				</div>
			</Menu.Dropdown>
		</Menu>
	);
};
